import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { SegmentedControlOptionType } from '@fullstory/components';
import { userOverlaySelectors } from '@fullstory/redux';
import { SegmentDefinition } from '@fullstory/proto/fs/api/segment';
import { EventSearch } from '@fullstory/proto/fs/api/search';
import {
  AllTimeRangeSegmentDefinition,
  ALL_SESSIONS,
  MATCHING_CLIPS,
  MATCHING_SESSIONS,
} from '../utils';

// NB(vasilios): Fixes "error TS2742: inferred type cannot be named without a reference"
// See https://github.com/aspect-build/rules_ts/issues/159 for more information.
import type {} from '@fullstory/redux-util/types';

type UseUserSessionConfigProps = {
  currentSegment: SegmentDefinition;
  currentEventSearch?: EventSearch;
};
const { getMatchingSessionsOrClips } = userOverlaySelectors;

export const useUserSessionConfig = ({
  currentSegment,
  currentEventSearch,
}: UseUserSessionConfigProps) => {
  const [sessionFilter, setSessionFilter] = useState<SegmentedControlOptionType | null>(null);
  const matchingSessionsOrClips = useSelector(getMatchingSessionsOrClips);

  // figure out if it should always be clips or always sessions
  const isClips = useMemo(
    () => (matchingSessionsOrClips.data?.Clips?.length ?? 0) > 0,
    [matchingSessionsOrClips.data],
  );
  const segmentedOptions = useMemo((): SegmentedControlOptionType[] => {
    if (isClips) return [ALL_SESSIONS, MATCHING_CLIPS];
    return [ALL_SESSIONS, MATCHING_SESSIONS];
  }, [isClips]);

  // once the user clips have loaded, and we actually get clips or sessions,
  // update the default filter to either be matching sessions or clips
  useEffect(() => {
    if (!sessionFilter && matchingSessionsOrClips.data) setSessionFilter(segmentedOptions[1]!);
  }, [segmentedOptions, sessionFilter, matchingSessionsOrClips.data]);

  // if it's set to all sessions, swap out to the all time range segment definition and no event search
  const { segmentDefinition, eventSearch } = useMemo(
    (): { segmentDefinition: SegmentDefinition; eventSearch?: EventSearch } =>
      sessionFilter === segmentedOptions[0]
        ? { segmentDefinition: AllTimeRangeSegmentDefinition }
        : { segmentDefinition: currentSegment, eventSearch: currentEventSearch },
    [currentSegment, currentEventSearch, sessionFilter, segmentedOptions],
  );

  // treat the matching sessions as the source of truth for all user related data
  const userDetails = useMemo(
    () => (matchingSessionsOrClips.loading ? null : matchingSessionsOrClips.data),
    [matchingSessionsOrClips.loading, matchingSessionsOrClips.data],
  );

  return {
    sessionFilter,
    setSessionFilter,
    segmentedOptions,
    segmentDefinition,
    eventSearch,
    userDetails,
    matchingSessionsOrClips,
  };
};
